// ============================================
// Góp ý của khách — hàng đợi xử lý phía cửa hàng (bug #208)
// ============================================
// Nguồn dữ liệu: `/feedback/staff/*` qua `hooks/useFeedback.ts`. Quyền BE là
// `IsStoreAdmin`.
//
// Thẻ tổng hợp và bảng dùng CHUNG một object `params` — `useStaffFeedbackSummary`
// tự bỏ `page`/`page_size` ra khỏi đó. Truyền hai bộ lọc khác nhau cho hai hook
// nghĩa là con số trên thẻ và số dòng trong bảng nói về hai tập bản ghi khác
// nhau, mà người dùng thì không có cách nào biết bên nào đúng.

import { useCallback, useMemo, useState } from 'react';
import type { StaffFeedback, UUID } from '@conai/shared-types';
import { PageContainer, PageHeader, Pagination } from '../../components/shared';
import { getApiErrorMessage } from '../../utils/apiError';
import {
  STAFF_FEEDBACK_DEFAULT_PAGE_SIZE,
  useStaffFeedbackList,
  useStaffFeedbackSummary,
} from '../../hooks/useFeedback';
import {
  FeedbackDetailModal,
  FeedbackFilterBar,
  FeedbackSummaryCards,
  FeedbackTable,
} from './components';
import { useFeedbackText } from './feedbackText';
import {
  EMPTY_FEEDBACK_FILTERS,
  buildFeedbackListParams,
  isFeedbackFilterActive,
  type FeedbackFilterState,
} from './filters';

function RefreshIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
      <path d="M16.5 10a6.5 6.5 0 1 1-1.9-4.6" />
      <path d="M16.5 3v3.5H13" strokeLinejoin="round" />
    </svg>
  );
}

export function FeedbackPage() {
  const { t, tr } = useFeedbackText();

  const [filters, setFilters] = useState<FeedbackFilterState>(EMPTY_FEEDBACK_FILTERS);
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<UUID | null>(null);

  /**
   * Đổi bộ lọc thì VỀ TRANG 1 trong cùng một lần render.
   *
   * Không gộp ở đây mà đi qua `useEffect` thì có một nhịp trung gian gửi lên
   * server "bộ lọc mới + trang cũ": người đang đứng ở trang 5 lọc lại còn 12 kết
   * quả sẽ thấy bảng trống một nhịp trước khi nhảy về trang 1 — và đó là một
   * request thừa, có kèm một cache entry thừa.
   */
  const patchFilters = useCallback((patch: Partial<FeedbackFilterState>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPage(1);
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(EMPTY_FEEDBACK_FILTERS);
    setPage(1);
  }, []);

  const params = useMemo(
    () => buildFeedbackListParams(filters, page, STAFF_FEEDBACK_DEFAULT_PAGE_SIZE),
    [filters, page]
  );

  const list = useStaffFeedbackList(params);
  // CÙNG object params — hook tự cắt phần phân trang.
  const summary = useStaffFeedbackSummary(params);

  const selectedRow = useMemo<StaffFeedback | undefined>(
    () => (selectedId ? list.items.find((item) => item.id === selectedId) : undefined),
    [list.items, selectedId]
  );

  const hasActiveFilters = isFeedbackFilterActive(filters);

  return (
    <PageContainer>
      <PageHeader
        title={tr('feedbackPageTitle', 'Customer feedback')}
        subtitle={tr('feedbackPageSubtitle', 'Reviews, product issues and app reports from customers')}
        actions={
          <button
            type="button"
            onClick={() => list.refetch()}
            disabled={list.isFetching}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshIcon />
            {list.isFetching ? t('loading') : tr('feedbackRefresh', 'Refresh')}
          </button>
        }
      />

      {list.isError ? (
        <div className="mt-6 rounded-xl border border-red-200 bg-red-50 p-4">
          <p className="text-sm font-semibold text-red-700">
            {tr('feedbackLoadError', 'Could not load customer feedback.')}
          </p>
          <p className="mt-1 text-sm text-red-600">{getApiErrorMessage(list.error, t)}</p>
          <button
            type="button"
            onClick={() => list.refetch()}
            className="mt-3 rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-50"
          >
            {tr('feedbackRetry', 'Try again')}
          </button>
        </div>
      ) : null}

      <div className="mt-6">
        {/* `summary` là một query RIÊNG: `/feedback/staff/summary/` hỏng được
            trong khi `/feedback/staff/` vẫn chạy. Bỏ qua `isError` ở đây thì
            khối thẻ lặng lẽ biến mất và thanh lọc hiện `0` cho cả bốn trạng
            thái — một lời khẳng định SAI ngay bên trên một cái bảng đầy dữ
            liệu. */}
        <FeedbackSummaryCards
          summary={summary.data}
          isLoading={summary.isLoading}
          isError={summary.isError}
          onRetry={() => summary.refetch()}
        />
      </div>

      <div className="mt-4">
        <FeedbackFilterBar
          filters={filters}
          // Dict THƯA — component tự `?? 0` cho từng trạng thái.
          statusCounts={summary.data?.by_status}
          openCount={summary.data?.open}
          // Summary lỗi -> ẩn badge đếm, KHÔNG hiện `0`. Chip vẫn bấm được vì
          // lọc chạy trên danh sách, không phụ thuộc summary.
          countsUnavailable={summary.isError}
          onChange={patchFilters}
          onReset={resetFilters}
          hasActiveFilters={hasActiveFilters}
        />
      </div>

      <div className="mt-4">
        <FeedbackTable
          items={list.items}
          isLoading={list.isLoading}
          onSelect={(row) => setSelectedId(row.id)}
        />
      </div>

      {list.count > 0 ? (
        <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <Pagination
            variant="numbered"
            currentPage={list.page}
            totalPages={list.totalPages}
            totalItems={list.count}
            // Số dòng/trang THẬT SỰ đã gửi lên BE (hook đã kẹp ≤ 100) — dùng lại
            // đúng con số đó để "Showing x of y" không lệch với dữ liệu về.
            pageSize={list.pageSize}
            onPageChange={setPage}
          />
        </div>
      ) : null}

      {selectedId ? (
        <FeedbackDetailModal
          feedbackId={selectedId}
          fallbackRecord={selectedRow}
          onClose={() => setSelectedId(null)}
        />
      ) : null}
    </PageContainer>
  );
}
